import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { CalendarBlank, TwitterLogo, InstagramLogo, TiktokLogo, FacebookLogo, YoutubeLogo, Trash, PaperPlaneRight } from '@phosphor-icons/react';

const PL = {
  twitter: { name: 'X (Twitter)', icon: TwitterLogo, needsMedia: false },
  instagram: { name: 'Instagram', icon: InstagramLogo, needsMedia: true },
  tiktok: { name: 'TikTok', icon: TiktokLogo, needsMedia: 'video' },
  facebook: { name: 'Facebook', icon: FacebookLogo, needsMedia: false },
  youtube: { name: 'YouTube', icon: YoutubeLogo, needsMedia: 'video' },
};

export default function Scheduler() {
  const [posts, setPosts] = useState([]);
  const [songs, setSongs] = useState([]);
  const [media, setMedia] = useState([]);
  const [publishing, setPublishing] = useState('');
  const [date, setDate] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [f, setF] = useState({ platform: 'twitter', content: '', hashtags: '', song_id: '', time: '18:00', media_url: '' });

  const load = () => {
    api.get('/scheduler/posts').then(r => setPosts(r.data));
    api.get('/songs').then(r => setSongs(r.data));
    api.get('/ai/library').then(r => setMedia(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const postsFor = (d) => posts.filter(p => dayKey(new Date(p.scheduled_at)) === dayKey(d));

  const create = async () => {
    if (!f.content) { toast.error('Write something first'); return; }
    const [h, m] = f.time.split(':');
    const dt = new Date(date); dt.setHours(+h, +m, 0, 0);
    try {
      await api.post('/scheduler/posts', {
        platform: f.platform, content: f.content,
        hashtags: f.hashtags.split(/[\s,]+/).filter(Boolean),
        song_id: f.song_id || undefined,
        media_url: f.media_url || undefined,
        scheduled_at: dt.toISOString(),
      });
      setShowAdd(false); setF({ ...f, content: '', hashtags: '', media_url: '' });
      toast.success('Post queued');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not queue the post');
    }
  };

  const publishNow = async (p) => {
    setPublishing(p.post_id);
    try {
      const { data } = await api.post('/social/publish', {
        targets: [p.platform], content: p.content, hashtags: p.hashtags || [],
        media_url: p.media_url || undefined, post_id: p.post_id,
      });
      const r = data.results[0];
      if (r.ok) toast.success(`Posted to ${PL[p.platform]?.name}${r.note ? ' — ' + r.note : ''}`, {
        action: r.url ? { label: 'View', onClick: () => window.open(r.url, '_blank') } : undefined,
      });
      else toast.error(r.error || 'Publish failed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
    finally { setPublishing(''); }
  };

  const del = async (id) => {
    await api.delete(`/scheduler/posts/${id}`);
    toast.success('Removed');
    load();
  };

  const daysWithPosts = posts.reduce((acc, p) => {
    const k = dayKey(new Date(p.scheduled_at));
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Scheduler</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Queue the drop.</h1>
          <p className="text-zinc-400 mt-1 text-sm">Plan it, then publish for real to X, Instagram, Facebook, TikTok and YouTube.</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="scheduler-new-btn" className="btn-neon rounded-full font-display h-10">+ New post</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle className="font-display">Schedule a post</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={f.platform} onValueChange={v => setF({ ...f, platform: v })}>
                <SelectTrigger data-testid="new-platform-select" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PL).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Textarea data-testid="new-content-input" placeholder="Caption / post content" value={f.content} onChange={e => setF({ ...f, content: e.target.value })} className="min-h-[100px] bg-zinc-950 border-zinc-800" />
              <Input data-testid="new-hashtags-input" placeholder="#hashtags #space #or #commas" value={f.hashtags} onChange={e => setF({ ...f, hashtags: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={f.song_id} onValueChange={v => setF({ ...f, song_id: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="Attach song (opt.)" /></SelectTrigger>
                  <SelectContent>
                    {songs.map(s => <SelectItem key={s.song_id} value={s.song_id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="new-time-input" type="time" value={f.time} onChange={e => setF({ ...f, time: e.target.value })} className="bg-zinc-950 border-zinc-800" />
              </div>
              <Select value={f.media_url} onValueChange={v => setF({ ...f, media_url: v })}>
                <SelectTrigger data-testid="new-media-select" className="bg-zinc-950 border-zinc-800"><SelectValue placeholder="Attach AI media (needed for IG / TikTok / YouTube)" /></SelectTrigger>
                <SelectContent>
                  {media.map(m => (
                    <SelectItem key={m.filename} value={`/api/media/${m.filename}`}>
                      [{m.kind === 'video' ? 'video' : 'image'}] {m.prompt?.slice(0, 40) || m.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {PL[f.platform]?.needsMedia && !f.media_url && (
                <div className="text-xs font-mono text-amber-400">{PL[f.platform].name} requires {PL[f.platform].needsMedia === 'video' ? 'a video' : 'an image or video'} to publish.</div>
              )}
              <div className="text-xs font-mono text-zinc-500">Scheduled for: {date.toDateString()} @ {f.time}</div>
            </div>
            <DialogFooter>
              <Button data-testid="new-post-save-btn" onClick={create} className="btn-neon rounded-full">Queue post</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarBlank size={18} className="text-[#ccff00]" />
            <span className="font-display">Pick a date</span>
          </div>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            className="text-zinc-200"
            modifiers={{ hasPost: Object.keys(daysWithPosts).map(k => new Date(k)) }}
            modifiersClassNames={{ hasPost: 'ring-1 ring-[#ccff00]/60 rounded-md' }}
          />
        </div>

        <div className="lg:col-span-3 space-y-4">
          <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
            <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">{date.toDateString()}</div>
            {postsFor(date).length === 0 && <div className="text-sm text-zinc-500 py-4">Nothing queued for this day.</div>}
            <div className="space-y-3 stagger-in">
              {postsFor(date).map(p => {
                const Icon = PL[p.platform]?.icon || TwitterLogo;
                return (
                  <div key={p.post_id} data-testid={`post-${p.post_id}`} className="border border-zinc-800 rounded-lg p-4 bg-zinc-950/60 flex gap-4">
                    <Icon size={20} className="text-[#ccff00] shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-zinc-500">
                        {new Date(p.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {PL[p.platform]?.name}
                        {p.status === 'posted' && <span className="ml-2 text-[#ccff00] uppercase">posted</span>}
                        {p.status === 'failed' && <span className="ml-2 text-red-400 uppercase">failed</span>}
                      </div>
                      <div className="mt-1 text-sm whitespace-pre-wrap">{p.content}</div>
                      {p.hashtags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {p.hashtags.map((h, i) => <span key={i} className="text-xs font-mono text-[#ccff00]">{h.startsWith('#') ? h : `#${h}`}</span>)}
                        </div>
                      )}
                      {p.status !== 'posted' && (
                        <button data-testid={`publish-now-${p.post_id}`} onClick={() => publishNow(p)} disabled={publishing === p.post_id}
                          className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-[#ccff00] hover:underline">
                          <PaperPlaneRight size={12} weight="fill" /> {publishing === p.post_id ? 'publishing…' : 'Publish now'}
                        </button>
                      )}
                    </div>
                    <button onClick={() => del(p.post_id)} className="text-zinc-500 hover:text-red-500 transition-colors" data-testid={`delete-${p.post_id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border border-zinc-800 rounded-xl bg-zinc-900/40 p-5">
            <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider mb-3">Upcoming (all days)</div>
            <div className="space-y-2 max-h-[300px] overflow-auto">
              {posts.slice(0, 20).map(p => {
                const Icon = PL[p.platform]?.icon || TwitterLogo;
                return (
                  <div key={p.post_id} className="flex items-center gap-3 py-2 border-b border-zinc-800/60 text-sm">
                    <Icon size={14} className="text-zinc-500" />
                    <span className="font-mono text-xs text-zinc-500 w-32">{new Date(p.scheduled_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="truncate flex-1">{p.content}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
